import { DicomMetadataStore } from '@ohif/core';
import { useImportedStudyStore } from '../../stores/useImportedStudyStore';

export type ImportedStudyQidoResult = {
  studyInstanceUid: string;
  date: string;
  description: string;
  modalities: string;
  instances: number;
  mrn: string;
  patientName: string;
  time: string;
  isImported: true;
};

export function isImportedStudyUID(studyInstanceUID?: string | null): boolean {
  if (!studyInstanceUID) {
    return false;
  }

  return useImportedStudyStore.getState().isImportedStudyUID(studyInstanceUID);
}

/**
 * QIDO-shaped study list entry for client-imported studies (no server call).
 */
export function getImportedStudyQidoSearchResults(
  studyInstanceUID: string
): ImportedStudyQidoResult[] {
  const study = useImportedStudyStore
    .getState()
    .studies.find(entry => entry.studyInstanceUID === studyInstanceUID);

  if (!study) {
    return [];
  }

  const firstInstance = DicomMetadataStore.getStudy(studyInstanceUID)?.series?.[0]?.instances?.[0] as
    | { PatientID?: string; PatientName?: string; StudyTime?: string }
    | undefined;

  return [
    {
      studyInstanceUid: study.studyInstanceUID,
      date: study.studyDate,
      description: study.studyDescription,
      modalities: study.modalities,
      instances: study.numInstances,
      mrn: firstInstance?.PatientID ? String(firstInstance.PatientID) : '',
      patientName: firstInstance?.PatientName ? String(firstInstance.PatientName) : '',
      time: firstInstance?.StudyTime ? String(firstInstance.StudyTime) : '',
      isImported: true,
    },
  ];
}

/**
 * Series list derived from in-memory metadata for imported studies.
 */
export function getImportedStudySeriesSearchResults(studyInstanceUID: string) {
  const study = DicomMetadataStore.getStudy(studyInstanceUID);

  if (!study?.series?.length) {
    return [];
  }

  return study.series.map(series => {
    const firstInstance = series.instances[0];

    return {
      studyInstanceUid: studyInstanceUID,
      seriesInstanceUid: firstInstance.SeriesInstanceUID,
      modality: firstInstance.Modality,
      seriesNumber: firstInstance.SeriesNumber,
      seriesDate: firstInstance.SeriesDate,
      numSeriesInstances: series.instances.length,
      description: firstInstance.SeriesDescription,
      isImported: true,
    };
  });
}
